// store/modules/session.js
import { RepositoryFactory } from "@/repositories/RepositoryFactory";
import router from "@/router/index";
import { debugLogRef } from "@/utils/debugLog";

const SessionsRepository = RepositoryFactory.get("sessions");

let heartbeatTimer = null;
const HEARTBEAT_INTERVAL =
  parseInt(import.meta.env.VITE_SESSION_HEARTBEAT_INTERVAL) || 180000;

const state = {
  sessionToken: localStorage.getItem("session_token") || null,
  sessionBlocked: false,
  isEnabled: true,
  isUserSessionBannerVisible: false,
};

const getters = {
  getSessionToken: (state) => state.sessionToken,
  isSessionEnabled: (state) => state.isEnabled,
  shouldDisplaySessionWarning: (state) => state.isUserSessionBannerVisible,
};

const mutations = {
  SET_SESSION_TOKEN(state, token) {
    state.sessionToken = token;
    if (token) {
      localStorage.setItem("session_token", token);
    } else {
      localStorage.removeItem("session_token");
    }
  },
  SET_SESSION_BLOCKED(state, value) {
    state.sessionBlocked = value;
  },
  SET_SESSION_ENABLED(state, enabled) {
    state.isEnabled = enabled;
  },
  SET_USER_SESSION_BANNER_VISIBLE(state, value) {
    state.isUserSessionBannerVisible = value;
  },
};

const actions = {
  initSession({ commit }, payload) {
    debugLogRef(
      "SESSION UNIQUE",
      "initSession: Démarrage initialisation session",
    );
    const enabled = payload.enabled !== undefined ? payload.enabled : false;
    debugLogRef("SESSION UNIQUE", "initSession: Feature enabled =", enabled);
    commit("SET_SESSION_ENABLED", enabled);

    const token = payload.token;
    if (token) {
      debugLogRef(
        "SESSION UNIQUE",
        "initSession: Token reçu",
        token.substring(0, 8) + "...",
      );
      commit("SET_SESSION_TOKEN", token);
    } else {
      debugLogRef("SESSION UNIQUE", "initSession: Aucun token reçu");
    }
  },

  startSession({ dispatch }, sessionInfo) {
    debugLogRef("SESSION UNIQUE", "startSession: Démarrage de la session");

    if (sessionInfo.enabled) {
      // Init session with token and enabled status
      dispatch("initSession", sessionInfo);

      // Setup BroadcastChannel for session synchronization
      dispatch("broadcastChannel/setupBroadcastChannel", null, { root: true });

      // Announce new session to other tabs
      dispatch("broadcastChannel/announceNewSession", null, { root: true });

      // Start heartbeat
      dispatch("startHeartbeat");
    }
  },

  clearSession({ commit, dispatch }) {
    debugLogRef(
      "SESSION UNIQUE",
      "clearSession: Suppression du token et arrêt heartbeat",
    );
    commit("SET_SESSION_TOKEN", null);
    dispatch("stopHeartbeat");
  },
  // ===== HEARTBEAT =====

  startHeartbeat({ dispatch }) {
    debugLogRef(
      "SESSION UNIQUE",
      "startHeartbeat: Démarrage du heartbeat de session",
    );
    if (!state.isEnabled) {
      debugLogRef(
        "SESSION UNIQUE",
        "startHeartbeat: Feature désactivée, heartbeat non démarré",
      );
      return;
    }

    // Nettoyer l'ancien timer si existe
    if (heartbeatTimer) {
      debugLogRef("SESSION UNIQUE", "startHeartbeat: Nettoyage ancien timer");
      clearTimeout(heartbeatTimer);
    }

    // Créer un nouveau timer de HEARTBEAT_INTERVAL minutes
    debugLogRef(
      "SESSION UNIQUE",
      "startHeartbeat: Timer programmé dans",
      HEARTBEAT_INTERVAL / 1000,
      "secondes",
    );
    heartbeatTimer = setTimeout(() => {
      dispatch("checkSession");
    }, HEARTBEAT_INTERVAL);
  },

  resetHeartbeat({ dispatch, state }) {
    debugLogRef("SESSION UNIQUE", "resetHeartbeat: Tentative de reset");
    if (!state.isEnabled || !state.sessionToken) {
      debugLogRef(
        "SESSION UNIQUE",
        "resetHeartbeat: Ignoré (enabled:",
        state.isEnabled,
        ", token:",
        !!state.sessionToken,
        ")",
      );
      return;
    }
    // Ne reset que si on a un token
    if (state.sessionToken) {
      debugLogRef("SESSION UNIQUE", "resetHeartbeat: Redémarrage du heartbeat");
      dispatch("startHeartbeat");
    }
  },

  stopHeartbeat() {
    debugLogRef("SESSION UNIQUE", "stopHeartbeat: Arrêt du heartbeat");
    if (heartbeatTimer) {
      clearTimeout(heartbeatTimer);
      heartbeatTimer = null;
      debugLogRef("SESSION UNIQUE", "stopHeartbeat: Timer supprimé");
    }
  },

  checkSession({ state, dispatch }) {
    debugLogRef("SESSION UNIQUE", "checkSession: Vérification de la session");
    const token = state.sessionToken;

    if (!token) {
      debugLogRef(
        "SESSION UNIQUE",
        "checkSession: Pas de token, arrêt du heartbeat",
      );
      dispatch("stopHeartbeat");
      return;
    }

    debugLogRef("SESSION UNIQUE", "checkSession: Envoi heartbeat au serveur");
    return SessionsRepository.heartbeat(token)
      .then((response) => {
        debugLogRef(
          "SESSION UNIQUE",
          "checkSession: Session valide",
          response.data,
        );
        // Session valide, relancer le timer pour 5 minutes
        dispatch("startHeartbeat");
      })
      .catch((error) => {
        // Session invalide
        if (error.response && error.response.status === 409) {
          debugLogRef(
            "SESSION UNIQUE",
            "checkSession: Conflit 409 - Session invalide",
          );
          dispatch("handleInvalidSession");
        } else {
          debugLogRef(
            "SESSION UNIQUE",
            "checkSession: Erreur réseau, retry dans 5min",
            error,
          );
          // Autre erreur (réseau, etc.), réessayer dans 5 min
          dispatch("startHeartbeat");
        }
      });
  },

  handleInvalidSession({ commit, dispatch }, context = {}) {
    debugLogRef(
      "SESSION UNIQUE",
      "handleInvalidSession: Session invalidée, source =",
      context.source || "api",
    );
    // Stop heartbeat
    dispatch("stopHeartbeat");

    if (context.source === "broadcast") {
      // if source = 'broadcast', block the page without logout
      debugLogRef(
        "SESSION UNIQUE",
        "handleInvalidSession: Blocage page (broadcast)",
      );
      commit("SET_SESSION_BLOCKED", true);
    } else {
      // if source = 'api', c'est une autre session ailleurs so logout
      debugLogRef(
        "SESSION UNIQUE",
        "handleInvalidSession: Logout et redirection (api)",
      );
      dispatch("users/logout", {}, { root: true });
      // Display session banner after logout
      commit("SET_USER_SESSION_BANNER_VISIBLE", true);

      setTimeout(() => {
        commit("notifications/UNBLOCK_NOTIFICATIONS", null, { root: true });
      }, 1000);
      router.push({ name: "HomePage" });
    }
  },
  dismissSessionBanner({ commit }) {
    commit("SET_USER_SESSION_BANNER_VISIBLE", false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
